import React, { useState, useRef, useEffect } from 'react';
import { CloseIcon, ArrowRightWhiteIcon } from '../icons';
import { apiService, ContactUsage, contactPagePath } from '../../services/apiService';
import { useUpgradeGate } from '../useUpgradeGate';
import { trackClick } from '../../utils/analytics';
import { ContactLimitNotice, ContactUsageLine, ContactBlockReason } from './ContactLimitNotice';

interface TechContact {
    name: string;
    email: string;
    role: string;
    contact_number?: string | null;
}

interface ContactUniversityModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPreview: (message: string, summaryType: 'personal' | 'rd', techManagerName: string) => void;
    techTitle: string;
    techId: string; // Always utech_technology_id for data fetching
    referenceNumber?: string; // Separate reference number for display
    researcherName: string;
    userName: string;
    personalSummary: string;
    rdSummary: string;
    /** Opens the concierge booking form (BookCallModal). */
    onTalkToConcierge?: () => void;
    /** Where the modal was opened (chat, saved_researcher, …): sent with the
     *  contacts request and the Talk to Concierge lead. */
    surface?: string;
    /** For the Talk to Concierge lead only. Without an e-mail the account's
     *  own is looked up when the link is clicked. */
    university?: string;
    userEmail?: string;
}

export const ContactUniversityModal: React.FC<ContactUniversityModalProps> = ({
    isOpen,
    onClose,
    onPreview,
    techTitle,
    techId,
    referenceNumber,
    researcherName,
    userName,
    personalSummary,
    rdSummary,
    onTalkToConcierge,
    surface,
    university,
    userEmail,
}) => {
    const [message, setMessage] = useState('');
    // Only professional summary is used now; dropdown for personal vs R&D is commented out
    const [summaryType, setSummaryType] = useState<'personal' | 'rd'>('personal');
    const [editableSummary, setEditableSummary] = useState<string>(personalSummary);
    const [techManagerName, setTechManagerName] = useState<string>(researcherName);
    const [isLoadingTechManager, setIsLoadingTechManager] = useState<boolean>(true);
    const [contacts, setContacts] = useState<TechContact[]>([]);
    const [copiedEmail, setCopiedEmail] = useState<string>('');
    // University-contact limit (Backend/contact_limits.py): usage while within
    // it, `blocked` once past it — then there is nobody to address, so the
    // notice replaces the contacts and Preview is held back.
    const [usage, setUsage] = useState<ContactUsage | null>(null);
    const [blocked, setBlocked] = useState<{ reason: ContactBlockReason; usage: ContactUsage | null } | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    // The technology whose contacts this opening already fetched. Chat and
    // Saved keep the modal mounted and only hide it for Preview; Edit shows it
    // again for the same technology — that is not a new opening, and fetching
    // again would log (and may e-mail the team about) a second one.
    const fetchedForRef = useRef<string>('');
    // Direct addresses are part of what the paid plan buys. The entitlement is
    // decided server-side; this only decides whether to render what it returns.
    const upgrade = useUpgradeGate();

    // Fetch tech manager details when modal opens
    useEffect(() => {
        // A slow reply for the previous technology must not land on this one.
        let cancelled = false;
        const fetchTechManagerDetails = async () => {
            if (!techId) fetchedForRef.current = '';   // a real close clears the technology
            if (!isOpen || !techId) {
                setIsLoadingTechManager(false);
                return;
            }
            if (fetchedForRef.current === techId) {
                // Back from Preview: keep what this opening already showed.
                setIsLoadingTechManager(false);
                return;
            }

            setIsLoadingTechManager(true);
            setUsage(null); setBlocked(null); setContacts([]);
            try {
                // The metered contacts endpoint: the four fields the details
                // endpoint used to carry, or usage instead once over the limit.
                // Recorded server-side as an opening, with where it was.
                const contactsResponse = await apiService.getTechnologyContacts(techId, { surface, purpose: 'open' });
                if (cancelled) return;
                if (contactsResponse.success) fetchedForRef.current = techId;
                const result = contactsResponse.success ? contactsResponse.data : undefined;
                if (result && !result.allowed) {
                    setBlocked({ reason: result.reason || 'limit_reached', usage: result.usage });
                    setContacts([]);
                    // Nothing to read a name from; don't leave the previous
                    // technology's manager in "Dear".
                    setTechManagerName(researcherName || 'Technology Manager');
                }
                if (result?.allowed) setUsage(result.usage);
                const data: any = result?.allowed ? result.contacts : null;
                if (data) {
                    // Collect every addressable contact once, de-duplicated by
                    // email: the named manager, the licensing office, and the
                    // catch-all address are frequently the same mailbox, and
                    // listing one address three times reads as a bug.
                    const collected: TechContact[] = [];
                    const seen = new Set<string>();
                    const add = (name: any, email: any, role: string, phone?: any) => {
                        const addr = typeof email === 'string' ? email.trim() : '';
                        if (!addr || seen.has(addr.toLowerCase())) return;
                        seen.add(addr.toLowerCase());
                        collected.push({
                            name: (typeof name === 'string' && name.trim()) || addr,
                            email: addr, role, contact_number: phone || null,
                        });
                    };
                    const rawManagers = data.tech_manager_details;
                    const managerList = Array.isArray(rawManagers)
                        ? rawManagers : rawManagers ? [rawManagers] : [];
                    managerList.forEach((m: any) => add(m?.name, m?.email, 'Technology Manager', m?.contact_number));

                    // Curated tech-transfer office (technologies.tto_office_details) from
                    // the reviewed Global Universities sheet; NULL means not curated yet.
                    // The endpoint may hand JSON columns back as strings, so accept an
                    // object, a JSON string, or nothing. This is what the send path
                    // (ChatSearchResults.handleSendEmail) routes TO when no named manager
                    // exists, so it must be what this modal shows as the recipient —
                    // otherwise the user is told one address and the mail goes to another.
                    const rawOffice = data.tto_office_details;
                    let ttoOffice: any = null;
                    try {
                        ttoOffice = typeof rawOffice === 'object' && rawOffice !== null
                            ? rawOffice
                            : (typeof rawOffice === 'string' && rawOffice.trim() ? JSON.parse(rawOffice) : null);
                    } catch (e) {
                        ttoOffice = null;
                    }
                    const ttoOfficeEmails: string[] = Array.isArray(ttoOffice?.emails)
                        ? ttoOffice.emails.filter((e: any) => typeof e === 'string' && e.trim())
                        : [];
                    // One row per address: the row key, the Copy button and the
                    // clipboard all take a single address, so a joined list would
                    // copy "a, b" and defeat the de-dupe against the legacy fields.
                    ttoOfficeEmails.forEach(e => add(ttoOffice?.name, e, 'Technology Transfer Office'));

                    const office = data.tech_manager_default;
                    add(office?.name, office?.email, 'Technology Transfer Office', office?.contact_number);
                    add('University enquiries', data.common_email, 'General enquiries');
                    setContacts(collected);

                    // 1. Try tech_manager_details (list or dict)
                    const techManagerDetails = data.tech_manager_details;
                    if (techManagerDetails) {
                        const detailsList = Array.isArray(techManagerDetails) ? techManagerDetails : [techManagerDetails];
                        const firstManager = detailsList.find((manager: any) => manager?.name);
                        if (firstManager?.name) {
                            setTechManagerName(firstManager.name);
                            setIsLoadingTechManager(false);
                            return;
                        }
                    }
                    
                    // 2. Fallback: curated office first, then tech_manager_default —
                    // the same order handleSendEmail resolves the TO address in.
                    // tech_manager_default is scraped, and for 7 universities its
                    // "name" is the university's own name (WARF's is a person).
                    if (ttoOfficeEmails.length > 0 && typeof ttoOffice?.name === 'string' && ttoOffice.name.trim()) {
                        setTechManagerName(ttoOffice.name.trim());
                        setIsLoadingTechManager(false);
                        return;
                    }
                    const techManagerDefault = data.tech_manager_default;
                    if (techManagerDefault?.name) {
                        setTechManagerName(techManagerDefault.name);
                        setIsLoadingTechManager(false);
                        return;
                    }
                    
                    // 3. Final fallback to researcherName
                    setTechManagerName(researcherName || 'Technology Manager');
                }
            } catch (error) {
                if (cancelled) return;
                console.warn('Could not fetch tech manager details:', error);
                setTechManagerName(researcherName || 'Technology Manager');
            } finally {
                if (!cancelled) setIsLoadingTechManager(false);
            }
        };

        fetchTechManagerDetails();
        return () => { cancelled = true; };
        // `surface` is left out on purpose: it only says where the opening
        // was, and a change to it must not count a second one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, techId, researcherName]);

    // Initialize editable summary from personalSummary only when it's empty,
    // so user edits are preserved across preview / close / reopen.
    useEffect(() => {
        if (isOpen && personalSummary && (!editableSummary || editableSummary.trim() === '')) {
            setEditableSummary(personalSummary);
        }
    }, [isOpen, personalSummary, editableSummary]);

    // Prevent auto-focus on textarea when modal opens
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            // Remove any auto-focus that might interfere with dropdown
            textareaRef.current.blur();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    /** The same `talk_to_concierge` lead ContactTTOModal posts (Admin -> Leads
     *  -> Talk to Concierge; may e-mail the team), with what was clicked and
     *  where. It needs the account's e-mail: the caller's when it passed one,
     *  else the profile's — chat and Saved don't carry it this far. Best
     *  effort: the booking form opens either way, without waiting. */
    const logTalkToConcierge = async () => {
        try {
            const uid = apiService.getStoredUserId() || undefined;
            let email = (userEmail || '').trim();
            let name = (userName || '').trim();
            if (!email && uid) {
                const res = await apiService.getProfile(uid);
                const u = res.success ? res.data?.user : undefined;
                email = String(u?.email || '').trim();
                if (!name) name = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
            }
            if (!email) return;
            await apiService.createConciergeCall({
                name: name || email,
                email,
                areas: [university || 'University'],
                // Same text as ContactTTOModal: older backends read it.
                message: `Clicked Talk to Concierge for "${techTitle}" (${techId})`,
                form_source: 'talk_to_concierge',
                tech_id: techId || undefined,
                tech_title: techTitle || undefined,
                university: university || undefined,
                surface: surface || undefined,
                page_url: contactPagePath() || undefined,
            }, uid);
        } catch { /* best effort */ }
    };

    const isMessageValid = message.length >= 50 && message.length <= 600;
    // Over the contact limit there is no address to send to (the send paths
    // refuse too); hold Preview back rather than let them write in vain.
    const canPreview = isMessageValid && !blocked;

    const handlePreviewClick = () => {
        if (blocked) return;
        // Don't allow preview if message is too short
        if (!isMessageValid) {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
            return;
        }
        // Use referenceNumber for display in email, fallback to techId if not available
        const displayId = referenceNumber || techId;
        // Use editable summary so preview and email use the user's current text (including any edits)
        const fullMessage = `I found your tech, titled ${techTitle} (${displayId}), on UniversityTech.io and would like to find out more. Here's how we could potentially collaborate, work with your researchers or commercialise your tech: ${message}\n\nHere's a short profile about us:\n\n${editableSummary}\n\nLook forward to hearing back from you\n${userName}`;
        onPreview(fullMessage, 'personal', techManagerName);
    };

    return (
        <div className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4 font-grotesk" role="dialog" aria-modal="true">
            <div className="relative w-[690px] max-h-[90vh] bg-white rounded-2xl flex flex-col">
                 <div className="absolute top-0 right-0 pt-6 pr-6">
                    <button onClick={onClose} className="flex p-2.5 justify-center items-center gap-2 rounded-full bg-grayscale-1200 shadow-custom-light">
                        <CloseIcon />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto hide-scrollbar p-6 flex flex-col gap-4">
                    <h2 className="text-grayscale-1300 font-grotesk text-sm font-medium">
                        You are one step away from contacting the university!
                    </h2>
                    <ContactUsageLine usage={blocked ? null : usage} className="-mt-3" />

                    {/* Over the university-contact limit: say so before they
                        write anything, with "Request more". */}
                    {blocked && (
                        <ContactLimitNotice key={techId} reason={blocked.reason} usage={blocked.usage}
                            userName={userName} techId={techId}
                            onUpgrade={() => {
                                // gate() with nothing allowed raises App's plans
                                // sheet and returns false; true means the
                                // entitlement lookup is failing and nothing
                                // opened, so stay put rather than vanish.
                                if (!upgrade.gate(false, 'university contact details')) onClose();
                            }} />
                    )}

                    <div className="flex items-center gap-2 py-2">
                        <span className="text-grayscale-900 font-grotesk text-sm">Subject:</span>
                        <span className="text-grayscale-1300 font-grotesk text-sm">Interest in tech ({referenceNumber || techId})</span>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-grayscale-1200 font-grotesk text-sm">Dear</label>
                        <div className="flex h-[42px] py-3 px-2.5 items-center gap-2.5 self-stretch rounded bg-alternate-bg">
                            {isLoadingTechManager ? (
                                <span className="text-grayscale-400 font-grotesk text-sm font-medium">Loading...</span>
                            ) : (
                                <span className="text-grayscale-600 font-grotesk text-sm font-medium">{techManagerName}</span>
                            )}
                        </div>
                    </div>

                    {/* Direct contacts — a paid-plan benefit. Free accounts
                        never reach this modal (the tech card gates first), but
                        this checks the entitlement rather than assuming that,
                        so a change to the gate upstream can't leak addresses. */}
                    {upgrade.ent.can_contact && contacts.length > 0 && (
                        <div className="flex flex-col gap-2 p-3 rounded bg-alternate-bg">
                            <span className="text-grayscale-1200 font-grotesk text-sm font-bold">
                                Direct contacts
                            </span>
                            {contacts.map(c => (
                                <div key={c.email} className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-grayscale-1300 font-grotesk text-sm font-medium truncate">
                                            {c.name}
                                        </div>
                                        <div className="text-grayscale-600 font-grotesk text-xs truncate">
                                            {c.role} · {c.email}
                                            {c.contact_number ? ` · ${c.contact_number}` : ''}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        aria-label={`Copy ${c.email}`}
                                        title={`Copy ${c.email}`}
                                        onClick={async () => {
                                            trackClick('contact_click_copy_email', {
                                                role: c.role, tech_id: techId, plan: upgrade.ent.plan || null,
                                            });
                                            try {
                                                await navigator.clipboard.writeText(c.email);
                                                setCopiedEmail(c.email);
                                                setTimeout(() => setCopiedEmail(''), 1800);
                                            } catch {
                                                // Clipboard needs a secure context and permission;
                                                // when it is refused, say so instead of showing
                                                // "Copied!" over an empty clipboard.
                                                setCopiedEmail('');
                                                window.prompt('Copy this address:', c.email);
                                            }
                                        }}
                                        className="shrink-0 px-2.5 py-1 rounded-full border border-primary-300 text-primary-700 font-grotesk text-xs hover:bg-primary-100/40"
                                    >{copiedEmail === c.email ? 'Copied!' : 'Copy'}</button>
                                </div>
                            ))}
                            {/* Opens our own concierge form. Deliberately not a
                                mailto: there is no concierge@ mailbox to write
                                to, and a dead address is worse than no link.
                                Rendered only where a handler exists, for the
                                same reason — the chat surface does not carry
                                modal state down this far. */}
                            {onTalkToConcierge && (
                            <button
                                type="button"
                                onClick={() => {
                                    trackClick('contact_click_talk_to_concierge', {
                                        tech_id: techId, plan: upgrade.ent.plan || null,
                                    });
                                    void logTalkToConcierge();
                                    onTalkToConcierge?.();
                                }}
                                className="self-start mt-1 text-primary-700 font-grotesk text-xs font-medium underline"
                            >Talk to our concierge instead →</button>
                            )}
                        </div>
                    )}

                    <p className="text-black font-grotesk text-sm">
                        I found your tech, titled <span className="font-bold font-grotesk">{techTitle}</span> ({referenceNumber || techId}), on UniversityTech.io and would like to find out more. Here's how we could potentially collaborate, work with your researchers or commercialise your tech:
                    </p>

                    <div className="flex flex-col gap-1 relative">
                        <label className="text-black font-grotesk text-sm italic">* This message will be sent as an email facilitated by UniversityTech.io</label>
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={600}
                            placeholder="Please describe how you would like to collaborate, work with researchers, or commercialize this technology... (minimum 50 characters)"
                            className={`h-[100px] p-3 self-stretch rounded bg-alternate-bg text-grayscale-1200 font-grotesk text-sm resize-none focus:outline-none focus:ring-2 ${
                                message.length > 0 && message.length < 50 
                                    ? 'focus:ring-red-500 border border-red-300' 
                                    : 'focus:ring-primary-500'
                            }`}
                        />
                        <div className="absolute bottom-2 right-2.5 flex items-center gap-2">
                            {message.length > 0 && message.length < 50 && (
                                <span className="text-red-500 text-xs font-medium">Min 50 chars</span>
                            )}
                            <span className={`text-xs italic font-light ${
                                message.length < 50 ? 'text-red-500' : 
                                message.length > 550 ? 'text-orange-500' : 'text-grayscale-600'
                            }`}>
                                {message.length}/600 char
                            </span>
                        </div>
                    </div>

                    {/* Professional vs R&D summary dropdown removed – only professional summary is used now
                    <div className="flex flex-col gap-1">
                        <label className="text-grayscale-1200 font-grotesk text-sm">This summary was pulled from your profile and will be included in the message. You can also edit it here.</label>
                        <div className="relative">
                            <select
                                value={summaryType}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setSummaryType(e.target.value as 'personal' | 'rd');
                                }}
                                onFocus={(e) => { e.stopPropagation(); }}
                                onClick={(e) => { e.stopPropagation(); }}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                                className="w-full h-[42px] py-3 px-2.5 rounded border border-primary-300 bg-white text-secondary-text text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
                            >
                                <option value="personal">Professional summary</option>
                                <option value="rd">R&D summary</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                <ChevronDownIcon className="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                    */}

                    <div className="flex flex-col gap-1 relative">
                        <label className="text-black font-grotesk text-sm">Here is my short introduction. This summary was pulled from your profile and will be included in the message. You can also edit it here:</label>
                        <textarea
                            value={editableSummary}
                            onChange={(e) => setEditableSummary(e.target.value)}
                            placeholder="Professional summary from your profile – edit as needed for this message"
                            className="p-3 self-stretch rounded bg-alternate-bg text-grayscale-1200 font-grotesk text-sm resize-y min-h-[100px] hide-scrollbar focus:outline-none focus:ring-2 focus:ring-primary-500 border border-grayscale-300"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-black font-grotesk text-sm">Look forward to hearing back from you</label>
                        <div className="flex h-[42px] py-3 px-2.5 items-center gap-2.5 self-stretch rounded bg-alternate-bg">
                            <span className="text-grayscale-1300 font-grotesk text-sm">{userName}</span>
                        </div>
                    </div>
                </div>

                <div className="p-6 pt-0">
                    {blocked && (
                        <p className="text-grayscale-1100 font-grotesk text-xs text-center mb-2">
                            {blocked.reason === 'upgrade_required'
                                ? 'Sending a message needs a trial or paid plan.'
                                : "This message can't be sent until you have more university contacts."}
                        </p>
                    )}
                    <button 
                        onClick={handlePreviewClick} 
                        disabled={!canPreview}
                        className={`flex py-2.5 px-4 justify-center items-center gap-2 self-stretch rounded-full shadow-custom-light w-full transition-colors ${
                            canPreview 
                                ? 'bg-primary-500 hover:bg-primary-600' 
                                : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        <span className={`text-center font-grotesk text-sm font-medium ${
                            canPreview ? 'text-white' : 'text-gray-500'
                        }`}>
                            Preview Message
                            {!blocked && !isMessageValid && message.length > 0 && ` (${50 - message.length} more chars needed)`}
                        </span>
                        {canPreview && <ArrowRightWhiteIcon />}
                    </button>
                </div>
            </div>
        </div>
    );
};